import type { ContactsCountry } from "app/pages/sales-intelligence/modules/company-analysis/types/contacts";
import type { NestedRegionGroup } from "app/pages/sales-intelligence/utils/location";
import {
    buildCountryRegionGroups,
    nestRegionGroups,
    OTHER_COUNTRIES_REGION_NAME,
} from "app/pages/sales-intelligence/utils/location";
import type { IRegionObject } from "app/services/RegionService";

export type ContactsCountryGroup = {
    region: IRegionObject;
    countries: ContactsCountry[];
    subGroups: ContactsCountryGroup[];
    memberCountries: ContactsCountry[];
    totalCount: number;
};

export type VisibleContactsCountryGroup = {
    group: ContactsCountryGroup;
    filteredCountries: readonly ContactsCountry[];
    filteredSubGroups: readonly VisibleContactsCountryGroup[];
};

const sortCountriesByCountDesc = (countries: readonly ContactsCountry[]): ContactsCountry[] =>
    [...countries].sort((a, b) => b.contactsCount - a.contactsCount);

const sumContactsCount = (countries: readonly ContactsCountry[]): number =>
    countries.reduce((sum, country) => sum + country.contactsCount, 0);

const sortSiblingGroups = (groups: ContactsCountryGroup[]): ContactsCountryGroup[] => {
    const otherGroup = groups.find((group) => group.region.name === OTHER_COUNTRIES_REGION_NAME);
    const regularGroups = groups.filter((group) => group.region.name !== OTHER_COUNTRIES_REGION_NAME);
    regularGroups.sort((a, b) => b.totalCount - a.totalCount);

    return otherGroup ? [...regularGroups, otherGroup] : regularGroups;
};

const decorateNestedGroup = (group: NestedRegionGroup<ContactsCountry>): ContactsCountryGroup => {
    const memberCountries = sortCountriesByCountDesc(group.memberCountries);

    return {
        region: group.region,
        countries: sortCountriesByCountDesc(group.countries),
        subGroups: sortSiblingGroups(group.subGroups.map(decorateNestedGroup)),
        memberCountries,
        totalCount: sumContactsCount(memberCountries),
    };
};

export const buildEmployeeLocationGroups = (
    countries: readonly ContactsCountry[],
    regions: readonly IRegionObject[],
    otherCountriesLabel: string,
): ContactsCountryGroup[] =>
    sortSiblingGroups(
        nestRegionGroups(buildCountryRegionGroups(regions, countries, otherCountriesLabel)).map(decorateNestedGroup),
    );

export const sumVisibleContactsCount = (visible: VisibleContactsCountryGroup): number => {
    const countsById = new Map<number, number>();

    const collect = (node: VisibleContactsCountryGroup): void => {
        for (const country of node.filteredCountries) {
            countsById.set(country.id, country.contactsCount);
        }
        for (const subGroup of node.filteredSubGroups) {
            collect(subGroup);
        }
    };

    collect(visible);

    let total = 0;
    for (const count of countsById.values()) {
        total += count;
    }
    return total;
};
